import React, { useEffect, useState, useRef, createElement } from 'react';
interface VideoPlayerProps {
  src: string;
  autoPlay?: boolean;
  controls?: boolean;
}
export function VideoPlayer({
  src,
  autoPlay = true,
  controls = true
}: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    setIsLoading(true);
    setError(null);
    // Check if browser supports HLS natively (Safari)
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.addEventListener('loadeddata', () => setIsLoading(false));
      video.addEventListener('error', () => {
        setError('Failed to load video');
        setIsLoading(false);
      });
    } else {
      // Load hls.js dynamically for other browsers
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/hls.js@latest';
      script.async = true;
      script.onload = () => {
        // @ts-ignore - Hls is loaded from CDN
        if (window.Hls && window.Hls.isSupported()) {
          // @ts-ignore
          const hls = new window.Hls({
            enableWorker: true,
            lowLatencyMode: true
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          hls.on(window.Hls.Events.MANIFEST_PARSED, () => {
            setIsLoading(false);
          });
          hls.on(window.Hls.Events.ERROR, (event: any, data: any) => {
            console.error('HLS Error:', data);
            if (data.fatal) {
              setError('Failed to load video stream');
              setIsLoading(false);
            }
          });
        } else {
          setError('Your browser does not support video playback');
          setIsLoading(false);
        }
      };
      script.onerror = () => {
        setError('Failed to load video player');
        setIsLoading(false);
      };
      document.body.appendChild(script);
      return () => {
        try {
          document.body.removeChild(script);
        } catch (e) {
          // Script might already be removed
        }
      };
    }
  }, [src]);
  if (error) {
    return <div className="w-full h-auto bg-gray-900 rounded-lg flex items-center justify-center p-12" data-id="element-64">
        <div className="text-center" data-id="element-65">
          <div className="text-red-400 text-lg mb-2" data-id="element-66">⚠️ Video Unavailable</div>
          <div className="text-gray-400 text-sm" data-id="element-67">{error}</div>
        </div>
      </div>;
  }
  return <div className="relative w-full h-auto" data-id="element-68">
      {isLoading && <div className="absolute inset-0 bg-gray-900 flex items-center justify-center" data-id="element-69">
          <div className="text-white text-lg" data-id="element-70">Loading video...</div>
        </div>}
      <video ref={videoRef} controls={controls} autoPlay={autoPlay} playsInline muted className="w-full h-auto" data-id="element-71">
        Your browser does not support the video tag.
      </video>
    </div>;
}